import { Component, Input, OnInit } from '@angular/core';
import { AlertInput, LoadingController, ModalController } from '@ionic/angular';
import { TimeRecord } from 'src/app/entities/timeRecord';
import { TOAST_CLASS } from 'src/app/enums/notification/notification.enum';
import { DataChangeEmitWho } from 'src/app/models/data-change-emit/data-change-emit-payload';
import { DeviceInfoService } from 'src/app/services/device-info.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { TimeService } from 'src/app/services/time.service';
import { TimeRecordService } from 'src/app/services/timeRecord.service';
import { UserService } from 'src/app/services/user.service';
import { AppUserService } from 'src/app/services/app-user.service';
import { DateTimeHelper } from 'src/app/utils/date-time-helper';
import { TimeFormDataValues } from '../time.component';
import { ActionValidationException, NetworkException } from 'src/app/utils/exceptions';
import { GlobalConstants } from 'src/app/constant/constant';
import { MileageLogComponent } from 'src/app/components/mileage-log/mileage-log.component';
import * as dayjs from 'dayjs';
import { LogService } from 'src/app/baseServices/logging/log.service';
import { ToastLoggingService } from 'src/app/baseServices/logging/toast-logging.service';
import { TimeEntryIntervalResolverService } from 'src/app/services/time-entry-interval-resolver.service';

@Component({
  selector: 'app-edit-time',
  templateUrl: './edit-time.component.html',
  styleUrls: ['./edit-time.component.scss'],
})
export class EditTimeComponent implements OnInit {
  @Input() recordId: number;
  userId: number;
  timeRecordToEdit: TimeRecord;
  isOpenTimeRecord: boolean;
  interval: number = GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES;

  formValues: TimeFormDataValues;

  title = 'Edit Time Record';

  isPageReady = false;

  constructor(
    private userService: UserService,
    private appUserService: AppUserService,
    private timeService: TimeService,
    private timeRecordService: TimeRecordService,
    private modalController: ModalController,
    private loadingController: LoadingController,
    private notificationsService: NotificationsService,
    private deviceInfoService: DeviceInfoService,
    private logService: LogService,
    private toastLoggingService: ToastLoggingService,
    private timeEntryIntervalResolverService: TimeEntryIntervalResolverService
  ) { }

  private get logContext() { return { recordId: this.timeRecordToEdit?.RecordId, id: this.timeRecordToEdit?.Id, startDateTime: this.formValues?.startDateTime, endDateTime: this.formValues?.endDateTime }; }

  async ngOnInit() {
    this.loadData();
  }

  onFormValuesChanged(values: TimeFormDataValues) {
    this.formValues = values;
  }

  async loadData() {
    this.userId = this.userService.getCurrentUserId();
    this.timeRecordToEdit = await this.timeRecordService.getTimeRecordByRecordId(this.recordId);
    await this.loadEffectiveInterval();
    this.isOpenTimeRecord = this.timeRecordToEdit.ClockIn && !this.timeRecordToEdit.ClockOut && (await this.timeRecordService.checkIsTimeRecordOpen(this.timeRecordToEdit));

    if (this.isOpenTimeRecord) {
      this.title = 'Edit Current Time Record';
    } else {
      this.title = 'Edit Existing Time Record';
    }

    this.isPageReady = true;
  }

  private async loadEffectiveInterval() {
    this.interval = await this.timeEntryIntervalResolverService.resolveIntervalForFipsStateId(
      this.timeRecordToEdit?.FIPSStateId,
      this.appUserService.currentUser?.IsExempt
    );
  }

  async onSave() {
    const loading = await this.loadingController.create({ message: 'Saving time record...' });
    let shouldShowMileageAlert = false;

    try {
      await loading.present();

      const localNow = DateTimeHelper.getLocalNow();
      const timeZoneId = DateTimeHelper.getTimeZoneId();
      const inputType = await this.deviceInfoService.getInputType();

      let promises: Promise<any>[] = [];
      const timeChanged = this.checkIsTimeChanged();
      const assignmentChanged = this.checkIsAssignmentChanged();

      if (timeChanged) {
        promises.push(
          this.timeService.updateTimeClocks(
            DataChangeEmitWho.Self,
            this.timeRecordToEdit.RecordId,
            {
              ClockIn: this.formValues.startDateTime,
              ClockInTimeZoneId: this.formValues.startTimeZoneId,
              ClockOut: this.formValues.endDateTime,
              ClockOutTimeZoneId: this.formValues.endTimeZoneId,
              CapturedDateTime: localNow,
              CapturedDateTimeTimeZoneId: timeZoneId,
              InputType: inputType,
            },
            false,
            this.timeRecordToEdit
          )
        );
      }

      if (assignmentChanged) {
        const assignmentPromise = this.timeService.updateAssignment(
          DataChangeEmitWho.Self,
          this.timeRecordToEdit.RecordId,
          {
            ProjectId: this.formValues.projectId,
            TaskId: this.formValues.taskId,
            LaborCategoryId: this.formValues.laborCategoryId,
            FIPSStateId: this.formValues.fipsStateId,
            InputType: inputType,
          },
          false,
          this.timeRecordToEdit
        ).then(async () => {
          const isChangingProjects = this.timeRecordToEdit.ProjectId !== this.formValues.projectId;
          if (!isChangingProjects) return;

          const newProjectAllowsMileageTracking = await this.timeService.checkProjectAllowsMileageTracking(this.formValues.projectId);
          const userHasOdometerReadings = await this.timeService.checkUserHasOdometerReadingsForDate(this.userId, this.timeRecordToEdit.Date);

          shouldShowMileageAlert = userHasOdometerReadings || newProjectAllowsMileageTracking;
        });
        promises.push(assignmentPromise);
      }

      await Promise.all(promises);
      await this.timeService.syncTimeData();

      this.logService.info('EditTime.SaveSuccess', this.logContext);
    } catch (e) {
      if (e instanceof NetworkException) {
        this.toastLoggingService.warn('EditTime.NetworkDeferred', 'Your change has been made locally. Please connect to Internet to upload this change.', this.logContext);
        this.onClose();
      } else {
        this.toastLoggingService.error('EditTime.SaveError', e, `Failed to edit time record. ` + e.message, this.logContext);
      }
    } finally {
      loading.dismiss();
      this.onClose();

      if (shouldShowMileageAlert) {
        await this.showMileageLogAlert();
      }
    }
  }

  async onDelete() {
    this.confirmDelete();
  }

  private async confirmDelete(): Promise<void> {
    const alert = await this.notificationsService.showAlert({
      header: 'Warning',
      message: 'You are about to delete your time record. This will delete your recorded time and any associated activities.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Continue',
          handler: () => this.delete(),
        },
      ],
    });
    await alert.present();
  }

  private async delete(): Promise<void> {
    this.logService.info('EditTime.DeleteButtonPressed', this.logContext, true);
    const loading = await this.loadingController.create({ message: 'Deleting time record...' });
    try {
      await loading.present();

      const inputType = await this.deviceInfoService.getInputType();

      await this.timeService.deleteTime(
        DataChangeEmitWho.Self,
        this.timeRecordToEdit.RecordId,
        {
          InputType: inputType,
        },
        false,
        this.timeRecordToEdit
      );

      await this.timeService.syncTimeData();

      this.onClose();
    } catch (e) {
      if (e instanceof NetworkException) {
        this.toastLoggingService.warn('EditTime.NetworkDeferred', 'Your change has been made locally. Please connect to Internet to upload this change.', this.logContext);
        this.onClose();
      } else if (e instanceof ActionValidationException) {
        this.toastLoggingService.warn('EditTime.ValidationBlocked', 'The date you are modifying has already been verified. Your supervisor will need to return your activity log to you before edits can be made.', this.logContext);
      } else {
        this.toastLoggingService.error('EditTime.DeleteError', e, `Failed to delete time record.`, this.logContext);
      }
    } finally {
      this.loadingController.dismiss();
    }
  }

  private checkIsTimeChanged() {
    return (
      this.timeRecordToEdit.ClockIn != DateTimeHelper.formatDateTime(this.formValues.startDateTime) ||
      (this.formValues.endDateTime != null && this.timeRecordToEdit.ClockOut !== DateTimeHelper.formatDateTime(this.formValues.endDateTime)) ||
      this.timeRecordToEdit.ClockInTimeZoneId != this.formValues.startTimeZoneId ||
      this.timeRecordToEdit.ClockOutTimeZoneId != this.formValues.endTimeZoneId
    );
  }

  private checkIsAssignmentChanged() {
    return (
      this.timeRecordToEdit.ProjectId != this.formValues.projectId ||
      this.timeRecordToEdit.TaskId != this.formValues.taskId ||
      this.timeRecordToEdit.LaborCategoryId != this.formValues.laborCategoryId ||
      this.timeRecordToEdit.FIPSStateId != this.formValues.fipsStateId
    );
  }

  private onClose() {
    this.modalController.dismiss();
  }

  private async showMileageLogAlert() {
    await this.notificationsService.showAlert({
      header: 'Warning',
      message: `Please review the Mileage Log to ensure your mileage is recorded correctly.`,
      buttons: [
        {
          text: 'Open Mileage Log',
          handler: () => this.openMileageLog(),
        },
        {
          text: 'OK',
        }
      ],
      suppression: {
        suppressKey: GlobalConstants.SUPPRESS_MILEAGE_LOG_ALERT_DATE,
        suppressDuration: 24,
        suppressLabel: 'Hide this message',
      },
    });
  }

  private async openMileageLog() {
    const modal = await this.modalController.create({
      component: MileageLogComponent,
      componentProps: {
        userId: this.userId,
        date: dayjs(this.timeRecordToEdit.Date),
        canEdit: true,
        allowClosure: !this.isOpenTimeRecord,
      },
    });
    await modal.present();
  }
}
